import { useEffect, useRef, useState } from "react";
import { Button } from "../ds";

// Ganze Klassennamen statt zusammengesetzter: ein verlorenes Leerzeichen
// ergaebe eine Klasse, die es nicht gibt, und das faellt erst im Betrieb auf.
const NOTE = "ds-copy-note";
const NOTE_OFF = "ds-copy-note is-hidden";

/** Wert anzeigen und in die Zwischenablage legen — für Schlüssel und IDs, die
 *  von Hand woanders eingetragen werden müssen. */
export function CopyValue({ value, className = "text-xs" }: { value: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <>—</>;
  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-2">
      <span className={`font-mono min-w-0 truncate ${className}`}>{value}</span>
      <Button
        size="sm"
        variant="ghost"
        iconName={copied ? "check" : "doc"}
        onClick={() =>
          void navigator.clipboard.writeText(value).then(
            () => setCopied(true),
            () => setCopied(false),
          )
        }
      >
        {copied ? "kopiert" : "kopieren"}
      </Button>
    </span>
  );
}

/** Wert per Klick kopieren, ohne Knopf daneben — für Tabellenzellen, die sonst
 *  jede Zeile um eine Schaltfläche breiter machen würden.
 *
 *  `copy` trennt Anzeige und Inhalt: in der Peer-Tabelle steht `10.88.0.34/32`,
 *  gebraucht wird beim Eintippen aber die nackte Adresse. Bei den Netzen
 *  dahinter ist es umgekehrt — dort gehört die Maske dazu. */
export function CopyOnClick({ value, copy }: { value: string; copy?: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ohne das Aufräumen setzt der Timer den Zustand einer Zeile, die es nach dem
  // Nachladen der Tabelle nicht mehr gibt.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const text = copy ?? value;
  return (
    <button
      type="button"
      className="ds-copy"
      title={`${text} kopieren`}
      onClick={() =>
        void navigator.clipboard.writeText(text).then(
          () => {
            setCopied(true);
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(() => setCopied(false), 1000);
          },
          () => setCopied(false),
        )
      }
    >
      <span className="ds-copy-stack">
        <span className={copied ? "is-hidden" : undefined}>{value}</span>
        <span className={copied ? NOTE : NOTE_OFF} aria-hidden={!copied}>
          kopiert
        </span>
      </span>
    </button>
  );
}
